import { CartPickupLocationModal } from "components/cart/CartPickupLocationModal";
import CheckoutProgress from "components/general/CheckoutProgress";
import HelpTooltip from "components/general/HelpTooltip";
import { Tab, TabContainer } from "components/general/Tabs";
import TitledContainer from "components/general/TitledContainer";
import { Box, Button, FormattedCurrency, H5, HStack, Icon, Input, ParagraphSmall, Radio, RadioGroup, RadioIndicator, RadioLabel, ScrollView, Text, Textarea, TextareaInput, Typography, View, VStack, InputField } from "components/ui";
import CheckBox from "expo-checkbox";
import { AlertCircle, ChevronDown, ChevronLeft, CircleIcon, Italic, LeafIcon, MapPin, TriangleAlertIcon, Truck } from "lucide-react-native";
import React, { useEffect, useRef, useState } from "react";
import { ActivityIndicator, Platform, TouchableOpacity } from "react-native";
import { CartCheckoutLayout } from "screens/CartCheckoutLayout";
import { useGetCartByIdQuery, useGetCartCurrentdeliveryinfoQuery, useGetCartCurrentQuery, useGetCompanyLocationsQuery, usePutCartByIdMutation } from "store/abcoApi";
import { useIsMobile } from "utils/hooks/useIsMobile";
import { useDataLayer } from "utils/hooks/useDataLayer";
import DismissableContent from "components/general/DismissableContent";
import DateSelect from "components/general/DateSelect";
import { useUser } from "@clerk/clerk-expo";
import { useToken } from "@gluestack-style/react";
import { SiteSelectButton } from "components/general/SiteSelectButton";
import { useLocalSearchParams } from "expo-router";

export default function Page() {
	const { id } = useLocalSearchParams<{ id: string }>();
	const { data: cart, isSuccess, isLoading } = id ? useGetCartByIdQuery({ id: parseInt(id) }) : useGetCartCurrentQuery();
	const [updateCart] = usePutCartByIdMutation();
	const isMobile = useIsMobile();
	const [tailLift, setTailLift] = useState(false);
	const [authorityToLeave, setAuthorityToLeave] = useState(false);
	const [saveDeliveryInstructions, setSaveDeliveryInstructions] = useState(false);
	const [selectedDispatchMethod, setSelectedDispatchMethod] = useState(cart?.shippingType === "Single" ? "normal" : (cart?.shippingType ?? "normal"));
	const [showAdditionalDetails, setShowAdditionalDetails] = useState(false);
	const [isPickupModalOpen, setIsPickupModalOpen] = useState(false);
	const [selectedLocation, setSelectedLocation] = useState<any>(null);
	const additionalDetailsRef = useRef("");
	const deliveryInstructionsRef = useRef("");
	const purchaseOrderNumberRef = useRef("");
	const { isSignedIn } = useUser();
	const { data: companyLocations } = useGetCompanyLocationsQuery();

	const { data: deliveryInfo } = useGetCartCurrentdeliveryinfoQuery();

	const updateSite = (site: any) => {
		if (!site || (!site.siteId && !site.id) || !cart?.id) {
			console.error("updateSite: Missing required data", { site, cartId: cart?.id });
			return;
		}

		updateCart({
			cart: {
				siteId: site.siteId || site.id,
			},
			id: cart.id,
		})
			.unwrap()
			.then(() => {
				console.log("updateSite", "success");
			})
			.catch((error) => {
				console.error("updateSite", "error", error);
			});
	};

	const handleUpdateAdditionalDetails = (text: string) => {
		additionalDetailsRef.current = text;
	};

	const handleUpdateDeliveryInstructions = (text: string) => {
		deliveryInstructionsRef.current = text;
	};

	const handleUpdatePoNumber = (text: string) => {
		purchaseOrderNumberRef.current = text;
	};

	const { addShippingInfo } = useDataLayer();
	const [hasPushedViewCart, setHasPushedViewCart] = useState(false);

	useEffect(() => {
		if (!cart || !isSuccess || hasPushedViewCart) return;

		addShippingInfo();

		setHasPushedViewCart(true);
	}, [cart, isSuccess, hasPushedViewCart, addShippingInfo]);

	useEffect(() => {
		if (isSuccess && cart) {
			setTailLift(cart.tailLiftRequired ?? false);
			setAuthorityToLeave(cart.authorityToLeave ?? false);
			setSaveDeliveryInstructions(cart.saveDeliveryInstructions ?? false);
			handleUpdateAdditionalDetails(cart.additionalInformation ?? "");
			handleUpdateDeliveryInstructions(cart.deliveryInstructions ?? "");
			setShowAdditionalDetails(cart?.additionalInformation ? cart.additionalInformation.trim() !== "" : false);
			handleUpdatePoNumber(cart?.purchaseOrderNumber ?? "");
			// Keep selectedDispatchMethod in sync with cart's shippingType
			if (cart.shippingType !== "Single" && cart.shippingType !== "pickup") {
				setSelectedDispatchMethod(cart.shippingType ?? "normal");
			}
		}
	}, [isSuccess, cart]);

	const immediateDispatchMethod = [
		{
			id: 3,
			value: "normal",
			icon: Truck,
			title: "Normal",
			description: "24hr dispatch",
			price: cart?.shippingType == "" || cart?.shippingType == "normal" ? cart?.freightCharge : null,
			disabled: false,
		},
		// {
		// 	id: 1,
		// 	value: "urgent",
		// 	icon: Siren,
		// 	title: "Urgent",
		// 	description: "4hr business delivery",
		// 	price: cart?.shippingType == "urgent" ? cart?.freightCharge : null,
		// 	disabled: cart?.locationType != "Metro",
		// },
		// {
		// 	id: 8,
		// 	value: "fastest",
		// 	icon: Siren,
		// 	title: "Fastest",
		// 	description: "2hr business delivery",
		// 	price: cart?.shippingType == "fastest" ? cart?.freightCharge : null,
		// 	disabled: cart?.locationType != "Metro",
		// },
	];

	const updateShippingType = (shippingTypeValue: string) => {
		updateCart({
			cart: {
				shippingType: shippingTypeValue,
			},
			id: cart?.id as number,
		})
			.unwrap()
			.then((response) => {
				console.log("updateCart", "success");
			})
			.catch((error) => {
				console.log("updateCart", "error");
				// TODO, when external shipping fails to give quotation:
				// make normal shipping the default
				// disable urgent/fastest shipping
			});
	};

	const handleDispatchTypeChange = (value: string) => {
		if (value == "immediate") {
			setSelectedDispatchMethod("normal");
			updateShippingType("normal");
		} else {
			updateShippingType(value);
		}
	};

	const handleDeliveryTabChange = (tab: string) => {
		if (tab === "Single") {
			tab = "normal";
		}

		updateCart({
			cart: {
				shippingType: tab,
			},
			id: cart?.id as number,
		});
	};

	const updateAdditionalDetails = () => {
		updateCart({
			cart: {
				additionalInformation: additionalDetailsRef.current,
			},
			id: cart?.id as number,
		})
			.unwrap()
			.then(() => {
				console.log("updateAdditionalDetails", "success");
			})
			.catch(() => {
				console.log("updateAdditionalDetails", "error");
			});
	};

	const updateDeliveryInstructions = () => {
		updateCart({
			cart: {
				deliveryInstructions: deliveryInstructionsRef.current,
			},
			id: cart?.id as number,
		})
			.unwrap()
			.then(() => {
				console.log("deliveryDetails", "success");
			})
			.catch(() => {
				console.log("deliveryDetails", "error");
			});
	};

	const updatePurchaseOrderNumber = () => {
		updateCart({
			cart: {
				purchaseOrderNumber: purchaseOrderNumberRef.current,
			},
			id: cart?.id as number,
		})
			.unwrap()
			.then(() => {
				console.log("updatePurchaseOrderNumber", "success");
			})
			.catch(() => {
				console.log("updatePurchaseOrderNumber", "error");
			});
	};

	const handleCheckboxChange = (field: "tailLiftRequired" | "authorityToLeave" | "saveDeliveryInstructions", value: boolean) => {
		// Update local state
		switch (field) {
			case "tailLiftRequired":
				setTailLift(value);
				break;
			case "authorityToLeave":
				setAuthorityToLeave(value);
				break;
			case "saveDeliveryInstructions":
				setSaveDeliveryInstructions(value);
				break;
		}

		updateCart({
			cart: {
				[field]: value,
			},
			id: cart?.id as number,
		})
			.unwrap()
			.then(() => {
				console.log(`updateAdditionalDetails (${field})`, "success");
			})
			.catch(() => {
				console.log(`updateAdditionalDetails (${field})`, "error");
			});
	};

	// const navigation = useNavigation();
	// console.log(cart);
	// React.useEffect(() => {
	// 	navigation.setOptions({
	// 		header: undefined,
	// 		headerTitle: "Cart",
	// 	 });
	//   }, [navigation]);
	// Add loading state to prevent null reference errors
	if (isLoading || !isSuccess || !cart) {
		return (
			<CartCheckoutLayout canEdit={true}>
				<Box flex={1} justifyContent="center" alignItems="center" padding={20}>
					<ActivityIndicator size={"large"} style={{ transform: [{ scaleX: 2 }, { scaleY: 2 }, { translateY: 25 }] }} />
				</Box>
			</CartCheckoutLayout>
		);
	}

	let resolvedWarningMain = useToken("colors", "warningbright");
	const PageContent = () => {
		return (
			<ScrollView keyboardShouldPersistTaps="handled">
				<Box paddingBottom={isMobile ? 24 : 0}>
					{isMobile && (
						<>
							<CheckoutProgress />
							{!cart?.siteId && !cart?.pickupLocationId && isSignedIn && cart && (
								<Box bg="$neutral25" gap={16} padding={24} borderRadius={6}>
									<Box flexDirection="row" gap={12} borderRadius={6} borderWidth={1} borderColor="$warningbright" bg="$warningdisabled" paddingVertical={6} paddingHorizontal={8}>
										<TriangleAlertIcon size={18} color={resolvedWarningMain} />
										{!cart?.siteId && !cart?.pickupLocationId && (
											<Box flex={1}>
												<ParagraphSmall>
													<Text style={{ fontFamily: "Roboto_700Bold" }}>Please select a site to continue. </Text>
													<Text>We noticed that you haven't chosen a site yet. Selecting the appropriate site helps us provide accurate information.</Text>
												</ParagraphSmall>
											</Box>
										)}
									</Box>
									<HStack alignItems="center" gap={16}>
										<Box flex={isMobile ? 1 : undefined}>
											<SiteSelectButton
												value={
													cart
														? {
																id: cart.siteId || 0,
																name: cart.siteName || "",
															}
														: { id: 0, name: "" }
												}
												title="Site"
												onSelect={updateSite}
											>
												<Button variant="primary-light" borderRadius={12} gap={4} justifyContent="flex-start">
													<ParagraphSmall
														sx={{
															color: "$basicwhite",
														}}
													>
														Select Site for Delivery
													</ParagraphSmall>
												</Button>
											</SiteSelectButton>
										</Box>
										<Box flex={isMobile ? 1 : undefined}>
											<Button borderRadius={12} gap={4} variant="outline" onPress={() => setIsPickupModalOpen(true)}>
												<ParagraphSmall
													sx={{
														color: "$primarylight",
													}}
												>
													Pickup order
												</ParagraphSmall>
											</Button>
										</Box>

										{!isMobile && (
											<CartPickupLocationModal
												isVisible={isPickupModalOpen}
												onClose={() => setIsPickupModalOpen(false)}
												onLocationSelect={(location) => {
													setSelectedLocation(location);
													updateCart({
														cart: {
															pickupLocationId: location.id,
															shippingType: "pickup",
														},
														id: cart?.id as number,
													});
													setIsPickupModalOpen(false);
												}}
												cartId={cart?.id as number}
												selectedLocationId={cart?.pickupLocationId || undefined}
											/>
										)}
									</HStack>
								</Box>
							)}
						</>
					)}

					<VStack paddingHorizontal={isMobile ? 12 : 0} gap={32}>
						<TitledContainer title="Select Dispatch Method">
							<Box paddingHorizontal={16}>
								<Box paddingVertical={16}>
									<TabContainer onChange={handleDeliveryTabChange} tabTitleSize={"full"} selectedTab={cart?.shippingType == "pickup" ? "pickup" : "Single"}>
										<Tab title="Delivery" id="Single">
											{!cart?.siteId && !cart?.pickupLocationId && isSignedIn && (
												<Box>
													<HStack gap={6} padding={12} borderRadius={12} borderColor="$errorbright" borderWidth={1} bg="$errorlight">
														<AlertCircle color="red" />
														<Typography sx={{ color: "$errorbright", fontFamily: "Roboto_700Bold" }}>Please select a delivery location to proceed.</Typography>
													</HStack>
												</Box>
											)}
											{cart?.siteId && (
												<Box gap={12}>
													<Typography weight="bold">Delivery address:</Typography>
													<Box
														// @ts-ignore
														width={"fit-content"}
														bg="$neutral50"
														borderWidth={1}
														padding={16}
														borderRadius={12}
													>
														<Button paddingLeft={isMobile ? 6 : 0} gap={8}>
															<VStack flex={1}>
																<HStack alignItems="center" gap={6}>
																	<Icon as={MapPin} color="$basicblack" />
																	<Typography weight="bold">{cart?.siteName}</Typography>
																</HStack>
																<Typography>{cart?.siteFullAddress}</Typography>
															</VStack>
														</Button>
													</Box>
												</Box>
											)}

											<RadioGroup value={cart?.shippingType == "Single" ? "Single" : "immediate"} onChange={handleDispatchTypeChange}>
												<Radio
													// sx={{
													// 	opacity: cart?.shippingType === "Single" ? 1 : 0.75,
													// }}
													paddingVertical={24}
													value="Single"
												>
													<RadioIndicator mr="$2">{cart?.shippingType === "Single" && <Icon fill="$primarylight" as={CircleIcon} />}</RadioIndicator>
													<RadioLabel>
														<HStack alignItems="center" gap={8}>
															<Box>
																<Text
																	fontWeight={"$bold"}
																	sx={{
																		color: cart?.shippingType === "Single" ? "$basicblack" : "$neutral500",
																	}}
																>
																	Single dispatch
																</Text>
															</Box>
															<HelpTooltip tip="Items will be dispatched when then entire order is available." />
															{deliveryInfo && <Typography.ParagraphSmall sx={{ color: "$textLight700" }}>{deliveryInfo?.latestDispatchDate}</Typography.ParagraphSmall>}
														</HStack>
													</RadioLabel>
												</Radio>
												{cart?.shippingType === "Single" && (
													<View>
														<Box marginBottom={16}>
															<DismissableContent
																icon={<Icon as={LeafIcon} w={24} h={24} color="$successmain" />}
																variant="success"
																text="A single dispatch order is a sustainability friendly option that allows all products from an order placed at the same site location to be dispatched together, reducing delivery costs and environmental impact."
															/>
														</Box>
														{/* <Box borderRadius={8} backgroundColor="$neutral25">
															<Typography sx={{ marginBottom: 8, fontSize: isMobile ? 14 : 16 }} weight="bold">
																Select dispatch date:
															</Typography>
															<Box maxWidth={385} marginBottom={16}>
																<DateSelect />
															</Box>
														</Box> */}
													</View>
												)}

												<Radio
													// sx={{
													// 	opacity: cart?.shippingType !== "Single" ? 1 : 0.75,
													// }}
													paddingVertical={24}
													value="immediate"
												>
													<RadioIndicator mr="$2">{cart?.shippingType !== "Single" && <Icon fill="$primarylight" as={CircleIcon} />}</RadioIndicator>
													<RadioLabel>
														<HStack alignItems="center" gap={8}>
															<Box>
																<Text
																	fontWeight={"$bold"}
																	sx={{
																		color: cart?.shippingType !== "Single" ? "$basicblack" : "$neutral500",
																	}}
																>
																	Immediate dispatch
																</Text>
															</Box>
															<HelpTooltip tip="Items will be dispatched as soon as they are available." />
															{deliveryInfo && <Typography.ParagraphSmall sx={{ color: "$textLight700" }}>{deliveryInfo?.earlierDispatchDate}</Typography.ParagraphSmall>}
														</HStack>
													</RadioLabel>
												</Radio>
												{cart?.shippingType !== "Single" && (
													<View>
														<Box flexDirection={isMobile ? "column" : "row"} gap={isMobile ? 12 : 43} justifyContent="space-between">
															{immediateDispatchMethod.map((method, index) => {
																return (
																	<Button
																		key={index}
																		onPress={() => {
																			setSelectedDispatchMethod(method.value);
																			updateShippingType(method.value);
																		}}
																		borderWidth={2}
																		borderColor={selectedDispatchMethod === method.value ? "$primarylight" : "$neutral100"}
																		borderRadius={16}
																		padding={24}
																		bg={method.disabled ? "$neutral100" : "$neutral25"}
																		flex={1}
																		disabled={method.disabled}
																	>
																		<Box>
																			<Box w={"$full"} h={"$full"}>
																				<VStack alignItems="center">
																					<Icon color={selectedDispatchMethod === method.value ? "$primarylight" : "$basicblack"} h={24} w={24} as={method.icon} />
																					<H5
																						sx={{
																							color: selectedDispatchMethod === method.value ? "$primarylight" : "$basicblack",
																						}}
																					>
																						{method.title}
																					</H5>
																					<ParagraphSmall weight="normal">{method.description}</ParagraphSmall>
																					{!method.disabled && (
																						<Typography>
																							<FormattedCurrency amount={method.price} />{" "}
																						</Typography>
																					)}
																					{method.disabled && <Typography> Location not supported </Typography>}
																				</VStack>
																			</Box>
																		</Box>
																	</Button>
																);
															})}
														</Box>
													</View>
												)}
											</RadioGroup>
										</Tab>
										<Tab title="Pickup" id="pickup">
											<Box paddingVertical={24}>
												<Box padding={isMobile ? 0 : 24} borderRadius={8} bg="$neutral25">
													<Box gap={12}>
														<Typography weight="bold">Your order will be available for pick up from;</Typography>
														<Box
															// @ts-ignore
															width={isMobile ? "$full" : "fit-content"}
															bg="$neutral50"
															borderWidth={1}
															padding={16}
															borderRadius={12}
														>
															<Button paddingLeft={isMobile ? 6 : 0} gap={8} onPress={() => setIsPickupModalOpen(true)}>
																<VStack>
																	<HStack alignItems="center" gap={6}>
																		<Icon as={MapPin} color="$basicblack" />
																		<Typography weight="bold">{companyLocations?.find((loc) => loc.id === cart?.pickupLocationId)?.name || "Select a location"}</Typography>
																	</HStack>
																	{!cart?.pickupLocationId && (
																		<ParagraphSmall weight="normal" sx={{ color: "$textLight700" }}>
																			Please select a pickup location
																		</ParagraphSmall>
																	)}
																</VStack>
															</Button>
														</Box>
														<CartPickupLocationModal
															isVisible={isPickupModalOpen}
															onClose={() => setIsPickupModalOpen(false)}
															onLocationSelect={(location) => {
																setSelectedLocation(location);
																updateCart({
																	cart: {
																		pickupLocationId: location.id,
																		shippingType: "pickup",
																	},
																	id: cart?.id as number,
																});
																setIsPickupModalOpen(false);
															}}
															cartId={cart?.id as number}
															selectedLocationId={cart?.pickupLocationId || undefined}
														/>

														<ParagraphSmall>You will receive a confirmation message when your order is ready to be collected.</ParagraphSmall>
													</Box>
												</Box>
											</Box>
										</Tab>
									</TabContainer>
								</Box>
							</Box>
						</TitledContainer>
						{/* <TitledContainer title="Delivery Locations">
							<Box paddingHorizontal={16}>
								<Box
									// @ts-ignore
									width={"fit-content"}
									paddingVertical={16}
								>
									<Button paddingLeft={isMobile ? 6 : 0} alignItems="flex-start" gap={8}>
										<Icon mt={4} as={MapPin} color="$basicblack" />
										<VStack>
											<Typography weight="bold">St Georges Terrace Conference Centre</Typography>
											<Typography>19 St Georges Terrace Perth WA 6000</Typography>
										</VStack>
									</Button>
								</Box>
								<Box paddingVertical={20}>
									<Button alignSelf="flex-start">
										<HStack alignItems="center" gap={8}>
											<Box>
												<Text sx={{ color: "$primary" }}>Deliver to another location</Text>
											</Box>
											<HelpTooltip tip="Items will be dispatched as soon as they are available." />
										</HStack>
									</Button>
								</Box>
							</Box>
						</TitledContainer> */}
						<TitledContainer title="Delivery Requirements">
							<Box borderRadius={8} padding={16} backgroundColor="$neutral25">
								<Box sx={{ marginBottom: 16, maxWidth: isMobile ? "auto" : 400 }}>
									<Typography sx={{ marginBottom: 8, fontSize: isMobile ? 14 : 16 }} weight="bold">
										PO number
										{cart.requiresPurchaseOrderNumber && (
											<>
												<Typography weight="normal"> (required) </Typography>
												<HelpTooltip tip="Purchase order number is required for this order" />
											</>
										)}
									</Typography>
									<Input padding={12} borderRadius={8} variant="outline" size="md">
										<InputField defaultValue={purchaseOrderNumberRef.current} onChangeText={handleUpdatePoNumber} onBlur={updatePurchaseOrderNumber} placeholder="Purchase Order Number" />
									</Input>
								</Box>

								<Typography sx={{ marginBottom: 8, fontSize: isMobile ? 14 : 16 }} weight="bold">
									Delivery instructions
								</Typography>
								<Box>
									<Textarea bg="$basicwhite" height={156} borderRadius={12}>
										<TextareaInput
											fontSize={14}
											placeholder="Enter delivery instructions for this order..."
											defaultValue={deliveryInstructionsRef.current}
											onChangeText={handleUpdateDeliveryInstructions}
											onBlur={updateDeliveryInstructions}
										/>
									</Textarea>
								</Box>
								<Box>
									<HStack gap={4} alignItems="center" marginTop={24} marginBottom={12}>
										<CheckBox color={"#2B6FBB"} value={tailLift} onValueChange={(newValue) => handleCheckboxChange("tailLiftRequired", newValue)} />
										<Typography>Tail lift required</Typography>
										<HelpTooltip tip="A tail lift is a mechanical device permanently fitted to the back of a truck and is used to raise and lower goods on and off the truck." />
									</HStack>
									<HStack gap={4} alignItems="center" marginBottom={12}>
										<CheckBox color={"#2B6FBB"} value={authorityToLeave} onValueChange={(newValue) => handleCheckboxChange("authorityToLeave", newValue)} />
										<Typography>Authority to leave delivery</Typography>
										<HelpTooltip tip="Authority to leave delivery" />
									</HStack>
									<HStack gap={4} alignItems="center" marginBottom={12}>
										<CheckBox color={"#2B6FBB"} value={saveDeliveryInstructions} onValueChange={(newValue) => handleCheckboxChange("saveDeliveryInstructions", newValue)} />
										<Typography>Save delivery instructions for site</Typography>
										<HelpTooltip tip="Save delivery instructions for site" />
									</HStack>
								</Box>
							</Box>
						</TitledContainer>
						<View
							borderRadius={12}
							backgroundColor="$basicwhite"
							sx={{
								shadowColor: "#000",
								shadowOffset: {
									width: 0,
									height: 1,
								},
								shadowOpacity: 0.18,
								shadowRadius: 1.0,
								elevation: 1,
							}}
						></View>
						<TitledContainer
							customTitle={() => (
								<TouchableOpacity
									style={{
										flexDirection: "row",
										alignItems: "center",
										justifyContent: "space-between",
										width: "100%",
									}}
									onPress={() => setShowAdditionalDetails(!showAdditionalDetails)}
								>
									<Text style={{ fontSize: 16, fontWeight: "bold" }}>Additional details (optional)</Text>
									{showAdditionalDetails ? <ChevronDown size={32} color="black" /> : <ChevronLeft size={32} color="black" />}
								</TouchableOpacity>
							)}
						>
							{showAdditionalDetails && (
								<View style={{ borderRadius: 8, padding: 16, backgroundColor: "#F5F5F5" }}>
									<Text style={{ marginBottom: 8, fontSize: 16, fontWeight: "bold" }}>Notes for customer success</Text>
									<View>
										<Textarea style={{ backgroundColor: "#FFFFFF", height: 156, borderRadius: 12 }}>
											<TextareaInput
												style={{ fontSize: 14 }}
												placeholder="Enter your notes for our customer success team..."
												defaultValue={additionalDetailsRef.current}
												onChangeText={handleUpdateAdditionalDetails}
												onBlur={updateAdditionalDetails}
											/>
										</Textarea>
									</View>
								</View>
							)}
						</TitledContainer>
						<View
							borderRadius={12}
							backgroundColor="$basicwhite"
							sx={{
								shadowColor: "#000",
								shadowOffset: {
									width: 0,
									height: 1,
								},
								shadowOpacity: 0.18,
								shadowRadius: 1.0,
								elevation: 1,
							}}
						></View>
					</VStack>
				</Box>
			</ScrollView>
		);
	};

	if (isMobile) {
		return (
			<CartCheckoutLayout canEdit={true}>
				<View style={{ flex: 1, overflow: "hidden" }} height={undefined}>
					<PageContent />
				</View>
			</CartCheckoutLayout>
		);
	}
	return (
		<CartCheckoutLayout canEdit={true}>
			<PageContent />
		</CartCheckoutLayout>
	);
}
